import { useState, useEffect, useCallback, type ReactNode } from "react";
import { captureError } from "@/lib/errorReporting";
import { useNavigate } from "react-router-dom";
import { useWallet } from "@solana/wallet-adapter-react";
import { useConnection } from "@solana/wallet-adapter-react";
import { LAMPORTS_PER_SOL } from "@solana/web3.js";
import { useAuth } from "@/providers/AuthProvider";
import { Icon, useToast } from "@/components/ui";
import WalletButton from "@/components/WalletButton";
import { type TokenLockData } from "@/components/TokenLockCard";
import noiseTexturePng from "@/assets/shapes/noise-texture.png";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080";

/* ════════════════════════════════════════════
   Wallet Page
   (Managed wallets are invisible to the user)
   ════════════════════════════════════════════ */
export default function WalletPage(): ReactNode {
    const navigate = useNavigate();
    const { publicKey, connected, wallet } = useWallet();
    const walletName = wallet?.adapter.name ?? "Wallet";
    const { connection } = useConnection();
    const { isAuthenticated, token, signOut } = useAuth();

    const address = publicKey?.toBase58() ?? "";
    const [solBalance, setSolBalance] = useState<number>(0);
    const [balanceLoading, setBalanceLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [userLocks, setUserLocks] = useState<(TokenLockData & { cabal?: { name: string; ticker: string; slug: string; imageUrl?: string } })[]>([]);
    const [locksLoading, setLocksLoading] = useState(false);
    const { toast } = useToast();

    // Fetch SOL balance
    useEffect(() => {
        async function fetchBalance() {
            if (!publicKey || !connection) return;
            setBalanceLoading(true);
            try {
                const lamports = await connection.getBalance(publicKey);
                setSolBalance(lamports / LAMPORTS_PER_SOL);
            } catch (err) {
                captureError("Failed to fetch balance:", err);
            } finally {
                setBalanceLoading(false);
            }
        }
        fetchBalance();
    }, [publicKey, connection]);

    // Fetch user's token locks across all cabals
    const fetchLocks = useCallback(() => {
        if (!token || !isAuthenticated) return;
        setLocksLoading(true);
        fetch(`${API_BASE}/api/cabals/locks/my-locks`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => (r.ok ? r.json() : { locks: [] }))
            .then((data) => setUserLocks(data.locks || []))
            .catch(() => setUserLocks([]))
            .finally(() => setLocksLoading(false));
    }, [token, isAuthenticated]);

    useEffect(() => {
        fetchLocks();
    }, [fetchLocks]);

    // Re-fetch when page becomes visible
    useEffect(() => {
        function onFocus() { fetchLocks(); }
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [fetchLocks]);

    function handleCopyAddress() {
        navigator.clipboard.writeText(address);
        setCopied(true);
        toast("Address copied to clipboard", "success", 2000);
        setTimeout(() => setCopied(false), 2000);
    }

    async function handleRefreshBalance() {
        if (!publicKey || !connection) return;
        setBalanceLoading(true);
        try {
            const lamports = await connection.getBalance(publicKey);
            setSolBalance(lamports / LAMPORTS_PER_SOL);
        } catch (err) {
            captureError("Failed to refresh balance:", err);
        } finally {
            setBalanceLoading(false);
        }
    }

    return (
        <div className="relative flex min-h-[100dvh] w-full flex-col overflow-hidden max-w-[430px] mx-auto bg-[#0a0a0a]">
            <main className="relative z-10 flex-1 flex flex-col px-[clamp(16px,2.6dvh,24px)] pt-[clamp(44px,6dvh,56px)] pb-[clamp(140px,18dvh,180px)]">
                {/* ─── Header ─── */}
                <div className="flex items-center justify-between mb-[clamp(16px,2.6dvh,24px)]">
                    <h1 className="text-[clamp(34px,4.5dvh,42px)] font-bold leading-[1.15] text-white tracking-[-0.84px]">
                        My<br />Wallet
                    </h1>
                    <Icon name="codev-logo" size={66} className="!w-[clamp(52px,7.1dvh,66px)] !h-[clamp(52px,7.1dvh,66px)]" />
                </div>

                {!connected ? (
                    /* ─── Not Connected ─── */
                    <div className="flex-1 flex flex-col items-center justify-center gap-[24px]">
                        {/* Wallet icon in bordered circle */}
                        <div className="size-[80px] rounded-full border-[0.5px] border-[rgba(153,153,153,0.5)] bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.1)] flex items-center justify-center">
                            <Icon name="wallet-default" size={32} inline className="text-[#6E6975]" />
                        </div>

                        {/* Text */}
                        <div className="text-center flex flex-col gap-[8px]">
                            <p className="text-white font-semibold text-[clamp(18px,2.1dvh,20px)]">
                                No Wallet Connected
                            </p>
                            <p className="text-[rgba(255,255,255,0.4)] text-[14px] font-medium leading-[1.4] max-w-[280px] mx-auto">
                                Connect your Solana wallet to start creating and joining codevs
                            </p>
                        </div>

                        <WalletButton variant="cta" />
                    </div>
                ) : (
                    /* ─── Connected ─── */
                    <div className="flex flex-col gap-[clamp(16px,2.1dvh,20px)]">
                        {/* ═══ Yellow Wallet Card ═══ */}
                        <div className="relative flex flex-col h-[clamp(170px,22.4dvh,209px)] justify-between overflow-clip rounded-[12px] pt-[24px] pb-[16px] px-[16px]">
                            {/* Card background layers */}
                            <div aria-hidden className="absolute inset-0 rounded-[12px] bg-[#f3ce28]" />
                            <div aria-hidden className="absolute -left-[18px] -top-[30px] w-[344px] h-[185px] bg-[#f1ce2f] blur-[36px]" />
                            <div
                                aria-hidden
                                className="absolute inset-0 rounded-[12px]"
                                style={{
                                    backgroundImage: `url('${noiseTexturePng}')`,
                                    backgroundSize: "264px 132px",
                                    backgroundPosition: "top right",
                                }}
                            />
                            <div aria-hidden className="absolute inset-0 pointer-events-none rounded-[inherit] shadow-[inset_0px_0px_44px_0px_rgba(255,255,255,0.05)]" />

                            {/* Top row: Wallet name + refresh */}
                            <div className="relative flex items-start justify-between w-full">
                                <div className="flex flex-col gap-[16px]">
                                    <p className="font-bold text-[clamp(18px,2.1dvh,20px)] text-[#0a0a0a]">
                                        {walletName}
                                    </p>
                                    <p className="font-extrabold text-[clamp(28px,3.9dvh,36px)] text-black leading-normal">
                                        {balanceLoading
                                            ? "..."
                                            : `${solBalance.toFixed(3)} SOL`}
                                    </p>
                                </div>
                                <button
                                    onClick={handleRefreshBalance}
                                    disabled={balanceLoading}
                                    className="backdrop-blur-[18px] bg-white border border-[rgba(255,255,255,0.2)] flex items-center overflow-clip p-[8px] rounded-[8px] disabled:opacity-40"
                                >
                                    <Icon
                                        name="refresh"
                                        size={16}
                                        className={balanceLoading ? "animate-spin" : ""}
                                    />
                                </button>
                            </div>

                            {/* Bottom row: Wallet-plus + address pill */}
                            <div className="relative flex items-center justify-between w-full">
                                <button
                                    onClick={() => signOut()}
                                    className="bg-[#0a0a0a] flex items-center justify-center rounded-[30px] size-[44px]"
                                    title="Disconnect"
                                >
                                    <Icon name="disconnect" size={16} />
                                </button>
                                <button
                                    onClick={handleCopyAddress}
                                    className="bg-[#0a0a0a] flex gap-[10px] items-center justify-end pl-[12px] pr-[8px] py-[6px] rounded-[30px]"
                                >
                                    <span className="font-semibold text-[16px] text-white">
                                        {address.slice(0, 3)}...{address.slice(-3)}
                                    </span>
                                    <div className="bg-[rgba(255,255,255,0.05)] flex items-center overflow-clip p-[8px] rounded-[36px]">
                                        <Icon name={copied ? "copy" : "copy"} size={16} />
                                    </div>
                                </button>
                            </div>
                        </div>

                        {/* ═══ Sign In Prompt (connected but not authenticated) ═══ */}
                        {!isAuthenticated && (
                            <div className="bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.1)] border-[0.5px] border-[rgba(153,153,153,0.5)] flex flex-col gap-[16px] items-center p-[24px] rounded-[12px]">
                                <p className="font-medium text-[14px] text-[rgba(255,255,255,0.4)] text-center leading-[1.4]">
                                    Sign a message to verify wallet ownership and view your tokens
                                </p>
                                <WalletButton variant="cta" />
                            </div>
                        )}

                        {/* ═══ Tokens Card ═══ */}
                        {isAuthenticated && (
                            <div className="bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.2)] border-[0.5px] border-[rgba(153,153,153,0.5)] flex flex-col gap-[24px] overflow-clip p-[16px] rounded-[12px]">
                                <p className="font-semibold text-[clamp(18px,2.1dvh,20px)] text-white">
                                    Tokens
                                </p>

                                {locksLoading ? (
                                    <div className="h-20 rounded-[8px] bg-white/5 animate-pulse" />
                                ) : userLocks.length === 0 ? (
                                    <div className="text-center py-6">
                                        <Icon name="wallet" size={32} className="opacity-10 mx-auto mb-2" />
                                        <p className="text-[14px] text-white/30 font-medium">
                                            No coins yet. Codev a coin to get started.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-[12px]">
                                        {userLocks.map((lock, i) => (
                                            <div key={lock.id}>
                                                <button
                                                    onClick={() => lock.cabal?.slug && navigate(`/codevs/${lock.cabal.slug}`)}
                                                    className="w-full text-left flex gap-[10px] items-center h-[44px]"
                                                >
                                                    {/* Token avatar */}
                                                    <div className="size-[36px] rounded-full overflow-hidden border-[1.5px] border-white/20 shrink-0">
                                                        {lock.cabal?.imageUrl ? (
                                                            <img
                                                                alt={lock.cabal.ticker}
                                                                className="w-full h-full object-contain"
                                                                src={lock.cabal.imageUrl}
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full bg-[#0a0a0a] flex items-center justify-center">
                                                                <span className="text-[14px] font-bold text-white">
                                                                    {(lock.cabal?.ticker || "?").toUpperCase().charAt(0)}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Ticker + status */}
                                                    <div className="flex-1 flex flex-col gap-[2px] min-w-0">
                                                        <p className="font-semibold text-[14px] text-white truncate">
                                                            {lock.cabal?.ticker || "Unknown"}
                                                        </p>
                                                        <p className="font-semibold text-[12px] text-white opacity-50 uppercase">
                                                            {lock.status === "withdrawn" ? "" : lock.status}
                                                        </p>
                                                    </div>

                                                    {/* Amount */}
                                                    <div className="flex flex-col gap-[2px] items-end">
                                                        <p className="font-semibold text-[14px] text-white tabular-nums">
                                                            {(Number(lock.lockedAmount) / 1e9).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                                        </p>
                                                        <p className="font-medium text-[12px] text-white opacity-50">
                                                            tokens
                                                        </p>
                                                    </div>
                                                </button>
                                                {/* Divider (except last item) */}
                                                {i < userLocks.length - 1 && (
                                                    <div className="h-px w-full bg-[rgba(255,255,255,0.05)] mt-[12px]" />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}
